import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import useAuthStore from 'store/useAuthStore';
import { motion } from 'framer-motion';
import { useMediaQuery } from 'react-responsive';
import { StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as SolidStarIcon } from '@heroicons/react/24/solid';
import SubMenu from './SubMenus';
import logo from 'assets/logo/logo-main.png';
import { buildMenuGroups } from 'route/menu';
import { createMenuAccessChecker } from 'helpers/menuAccess';
import { expandMenuKeyAliases } from 'helpers/menuPermissionMap';
import { ChatContext } from '../../../pages/chat/context/provider';
import { NotificationManager } from 'components/react-notifications';
import { requestData } from 'helpers/request';

const Index = ({ onClose, show }) => {
  const isTabletMid = useMediaQuery({ query: '(max-width: 767px)' });
  const sidebarRef = useRef();
  const { pathname } = useLocation();
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [isSavingFavorite, setIsSavingFavorite] = useState(false);
  const { user, token, authSet } = useAuthStore();
  const { state } = React.useContext(ChatContext);
  const unreadTotal = Number(state?.unread?.total || 0);
  const unreadCustomer = Number(state?.unread?.customer || 0);
  const unreadGuest = Number(state?.unread?.guest || 0);
  const menuDefinitions = buildMenuGroups({
    total: unreadTotal,
    customer: unreadCustomer,
    guest: unreadGuest,
  });

  useEffect(() => {
    if (isTabletMid) onClose();
  }, [isTabletMid, pathname, onClose]);

  useEffect(() => {
    if (!isTabletMid || !show) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isTabletMid, onClose, show]);

  const Nav_animation = isTabletMid
    ? {
        open: {
          x: 0,
          width: '16rem',
          transition: { damping: 40 },
        },
        closed: {
          x: -250,
          width: 0,
          transition: { damping: 40, delay: 0.15 },
        },
      }
    : {
        open: {
          width: '16rem',
          transition: { damping: 40 },
        },
        closed: {
          width: '0',
          transition: { damping: 0 },
        },
      };

  const hasAccess = createMenuAccessChecker(user);

  const normalizeMenuId = (menuId) =>
    String(menuId || '')
      .replace(/^\/+|\/+$/g, '')
      .replace(/-/g, '_')
      .toLowerCase();

  const isFavorite = (menuId) => {
    if (!menuId) return false;

    const menuIds = new Set(expandMenuKeyAliases(menuId).map(normalizeMenuId).filter(Boolean));
    menuIds.add(normalizeMenuId(menuId));

    return (user?.permissions || []).some((permission) => {
      const isFavoriteValue = ['1', 'true'].includes(
        String(permission.menu_favorit ?? '')
          .trim()
          .toLowerCase()
      );

      return isFavoriteValue && menuIds.has(normalizeMenuId(permission.level_menu_limited));
    });
  };

  const filterMenuItems = (items) => {
    return (items || []).reduce((acc, item) => {
      // Filter sub-menu items based on user permissions
      const filteredSubs =
        item.subs && item.subs.length > 0
          ? item.subs
              .filter((subItem) => hasAccess(subItem.id, subItem.to) && !isFavorite(subItem.id))
              .map((subItem) => {
                // Also filter nested sub-items (third level)
                const filteredNestedSubs =
                  subItem.subs && subItem.subs.length > 0
                    ? subItem.subs.filter((nestedItem) => hasAccess(nestedItem.id, nestedItem.to))
                    : subItem.subs;
                return {
                  ...subItem,
                  subs: filteredNestedSubs,
                };
              })
              .filter((subItem) => {
                // Keep sub-item if has direct access or has visible nested items
                const subHasAccess = hasAccess(subItem.id, subItem.to);
                const hasVisibleNested = subItem.subs && subItem.subs.length > 0;
                return subHasAccess || hasVisibleNested;
              })
          : [];

      const hasDirectAccess =
        Boolean(item.to) && hasAccess(item.id, item.to) && !isFavorite(item.id);

      if (hasDirectAccess || filteredSubs.length > 0) {
        acc.push({
          ...item,
          subs: filteredSubs,
        });
      }

      return acc;
    }, []);
  };

  const menuGroups = menuDefinitions.reduce((acc, { title, items }) => {
    const filteredItems = filterMenuItems(items);
    if (filteredItems.length > 0) {
      acc.push({ title, items: filteredItems });
    }
    return acc;
  }, []);

  const favoriteMenus = menuDefinitions
    .flatMap(({ items }) =>
      (items || []).flatMap((item) => {
        const favorites = [];

        if (!item.subs?.length && item.to && isFavorite(item.id)) {
          favorites.push(item);
        }

        (item.subs || []).forEach((subItem) => {
          if (!subItem.subs?.length && subItem.to && isFavorite(subItem.id)) {
            favorites.push(subItem);
          }
        });

        return favorites;
      })
    )
    .filter(
      (item, index, items) =>
        item.id && items.findIndex((candidate) => candidate.id === item.id) === index
    );

  const dashboardGroups = menuGroups.filter((group) => group.title === 'Dashboard');
  const otherGroups = menuGroups.filter((group) => group.title !== 'Dashboard');
  const displayMenuGroups = [
    ...dashboardGroups,
    ...(favoriteMenus.length > 0
      ? [{ title: 'Favorit', items: favoriteMenus, isFavoriteGroup: true }]
      : []),
    ...otherGroups,
  ];

  const favoriteMenu = async (menuId, favorite) => {
    if (isSavingFavorite || !menuId) return;

    if (!token) {
      NotificationManager.error('Sesi tidak ditemukan. Silakan masuk kembali.', 'Gagal');
      return;
    }

    setIsSavingFavorite(true);

    try {
      const response = await requestData('favorite-menu', {
        menu: menuId,
        favorite,
        api_token: token,
      });

      if (!response?.success) {
        NotificationManager.error(
          response?.message || 'Tidak dapat memperbarui menu favorit.',
          'Gagal'
        );
        return;
      }

      const matchingMenuIds = new Set(expandMenuKeyAliases(menuId).map(normalizeMenuId));
      const updatedPermissions =
        response.permissions ||
        response.data?.permissions ||
        (user?.permissions || []).map((permission) =>
          matchingMenuIds.has(normalizeMenuId(permission.level_menu_limited))
            ? { ...permission, menu_favorit: favorite }
            : permission
        );
      const updatedUser = {
        ...user,
        ...(response.data || {}),
        permissions: updatedPermissions,
      };

      authSet(response.api_token || token, updatedUser);
      NotificationManager.success(
        favorite ? 'Menu ditambahkan ke favorit.' : 'Menu dihapus dari favorit.',
        'Berhasil'
      );
    } catch (error) {
      NotificationManager.error(error?.message || 'Tidak dapat memperbarui menu favorit.', 'Gagal');
    } finally {
      setIsSavingFavorite(false);
    }
  };

  return (
    <>
      {isTabletMid && show && (
        <button
          type='button'
          onClick={onClose}
          aria-label='Tutup navigasi'
          className='fixed inset-0 z-[998] border-0 bg-slate-950/40 md:hidden'
        />
      )}
      <motion.aside
        ref={sidebarRef}
        id='primary-sidebar'
        aria-label='Navigasi utama'
        aria-hidden={!show}
        variants={Nav_animation}
        initial={{ x: isTabletMid ? -256 : 0 }}
        animate={show ? 'open' : 'closed'}
        className='fixed inset-y-0 left-0 z-[999] flex h-dvh w-64 max-w-64 flex-col overflow-hidden border-r border-slate-200 bg-white text-gray'
      >
        <div className='flex h-16 shrink-0 items-center gap-3 border-b border-slate-200 bg-transparent px-3 py-2 font-medium'>
          <img src={logo} width={80} alt='Pulsalink' className='ml-2 mr-1' />
        </div>

        <nav
          aria-label='Menu utama'
          className={`min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain p-2 ${
            show ? 'block' : 'hidden'
          }`}
        >
          <div className='flex min-w-0 flex-col whitespace-normal text-[0.9rem] font-medium'>
            {displayMenuGroups.map((group, groupIndex) => (
              <section
                key={group.title}
                aria-labelledby={`sidebar-group-${groupIndex}`}
                className='min-w-0'
              >
                <h2
                  id={`sidebar-group-${groupIndex}`}
                  className={[
                    'mt-4 flex min-h-6 w-full items-center justify-start text-left text-xs font-semibold uppercase text-slate-500',
                    group.isFavoriteGroup ? 'gap-1.5 tracking-wide' : '',
                  ].join(' ')}
                >
                  {group.isFavoriteGroup && (
                    <SolidStarIcon aria-hidden='true' className='h-3.5 w-3.5 text-amber-500' />
                  )}
                  <span>{group.title}</span>
                  {group.isFavoriteGroup && (
                    <span className='ml-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium leading-4 text-slate-500'>
                      {group.items.length}
                      <span className='sr-only'> menu favorit</span>
                    </span>
                  )}
                </h2>
                <ul className='mt-2 flex min-w-0 flex-col gap-y-1'>
                  {group.items.map((item, index) => (
                    <React.Fragment key={item.id || `${groupIndex}-${index}`}>
                      {item.subs && item.subs.length === 0 ? (
                        <li className='group/menu-item flex min-w-0 items-center gap-px'>
                          <NavLink
                            to={`${item.to}`}
                            title={item.label}
                            className={({ isActive }) =>
                              [
                                'link button button--text flex min-w-0 flex-1 items-center gap-2! px-2 text-left',
                                isActive
                                  ? 'active bg-primary-light! font-semibold text-primary! hover:bg-primary-light!'
                                  : '',
                              ].join(' ')
                            }
                          >
                            {({ isActive }) => (
                              <>
                                <span
                                  className={
                                    isActive ? 'shrink-0 text-primary' : 'shrink-0 text-gray-500'
                                  }
                                >
                                  {item.icon || <StarIcon aria-hidden='true' className='h-4 w-4' />}
                                </span>
                                <span
                                  className={[
                                    'min-w-0 flex-1 truncate text-xs tracking-wide capitalize',
                                    isActive ? 'font-semibold text-primary' : 'text-gray-900',
                                  ].join(' ')}
                                >
                                  {item.label}
                                </span>
                              </>
                            )}
                          </NavLink>
                          <button
                            type='button'
                            aria-label={
                              isFavorite(item.id)
                                ? 'Hapus ' + item.label + ' dari favorit'
                                : 'Tambahkan ' + item.label + ' ke favorit'
                            }
                            title={
                              isFavorite(item.id)
                                ? 'Hapus ' + item.label + ' dari favorit'
                                : 'Tambahkan ' + item.label + ' ke favorit'
                            }
                            disabled={isSavingFavorite || !item.id}
                            onClick={() => favoriteMenu(item.id, !isFavorite(item.id))}
                            className={[
                              'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-1 disabled:cursor-wait disabled:opacity-50',
                              group.isFavoriteGroup
                                ? 'text-amber-600 hover:bg-amber-100 hover:text-amber-700'
                                : 'md:opacity-0 md:group-hover/menu-item:opacity-100 md:group-focus-within/menu-item:opacity-100',
                              !group.isFavoriteGroup && isFavorite(item.id)
                                ? 'text-amber-600 hover:bg-amber-50 hover:text-amber-700'
                                : !group.isFavoriteGroup
                                  ? 'text-gray-400 hover:bg-amber-50 hover:text-amber-600'
                                  : '',
                            ].join(' ')}
                            aria-pressed={isFavorite(item.id)}
                          >
                            {isFavorite(item.id) ? (
                              <SolidStarIcon aria-hidden='true' className='h-4 w-4' />
                            ) : (
                              <StarIcon aria-hidden='true' className='h-4 w-4' />
                            )}
                          </button>
                        </li>
                      ) : (
                        (show || isTabletMid) && (
                          <SubMenu
                            data={item}
                            isOpen={openSubMenu === item.id}
                            onToggle={() =>
                              setOpenSubMenu((prev) => (prev === item.id ? null : item.id))
                            }
                            favoriteMenu={favoriteMenu}
                            isFavorite={isFavorite}
                            isSaving={isSavingFavorite}
                          />
                        )
                      )}
                    </React.Fragment>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </nav>
      </motion.aside>
    </>
  );
};

export default Index;
